import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/utils';
import {
  primaryText,
  secondaryText,
  semiBoldText,
  smallTextSize,
} from '../../../styling/stylingHelper';
import { AdminLayout } from './AdminLayout';
import { ReportSettingsSection, reportTypeLabel } from './communication/ReportSettingsSection';
import { SETTINGS_REPORT_TYPES } from '../../../services/admin/reportsAdminService';

/** Standing communication configuration — the send surfaces live on the Send Reports tab. */
const AdminCommunicationSettingsPage = () => {
  // Super-admin tooling is always English regardless of the app's selected language.
  const { i18n } = useTranslation();
  const t = useMemo(() => i18n.getFixedT('en'), [i18n]);

  const [reportType, setReportType] = useState(SETTINGS_REPORT_TYPES[0]);

  // With a single configurable report type the tab row would be a no-op, so it stays hidden
  // until phase 4 widens SETTINGS_REPORT_TYPES.
  const showTypeTabs = SETTINGS_REPORT_TYPES.length > 1;

  /*
    Which report type is being configured has to stay visible while scrolling a long account list,
    so the switcher rides in the layout's sticky header instead of the page body. The active tab is
    solid purple — the previous white-on-grey pill was nearly invisible at a glance.
  */
  const typeTabs = showTypeTabs ? (
    <div className="flex gap-1 p-1 rounded-lg bg-gray-100 dark:bg-gray-800 w-fit max-w-full overflow-x-auto">
      {SETTINGS_REPORT_TYPES.map((type) => (
        <button
          key={type}
          onClick={() => setReportType(type)}
          className={cn(
            'px-3 py-1.5 rounded-md transition-colors shrink-0 whitespace-nowrap',
            smallTextSize,
            semiBoldText,
            type === reportType
              ? 'bg-purple-600 dark:bg-purple-600 text-white dark:text-white shadow-sm'
              : cn(secondaryText, 'hover:bg-white dark:hover:bg-gray-700/50')
          )}
        >
          {reportTypeLabel(t, type)}
        </button>
      ))}
    </div>
  ) : undefined;

  return (
    <AdminLayout stickyBar={typeTabs}>
      {/* Tight vertical rhythm on purpose: the settings list is the page. */}
      <div className="space-y-4">
        <h2 className={cn(primaryText, 'text-lg', semiBoldText)}>
          {t('adminTesting.reportSettings.pageTitle')}
        </h2>

        {/* Keyed so switching tabs remounts: without it the previous type's toggles paint for a
            frame under the new heading, and the role helper keeps its picked roles and its
            "Updated N" message — both of which read as belonging to the type now on screen. */}
        <ReportSettingsSection key={reportType} reportType={reportType} />
      </div>
    </AdminLayout>
  );
};

export default AdminCommunicationSettingsPage;
